import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatLongDate, STATUS_IT } from "@/lib/format";
import { AdminButton, AdminCard, AdminField, EmptyState, adminInput } from "@/components/admin/ui";
import { ReservationRow } from "@/pages/admin/AdminToday";
import ReservationDialog from "@/pages/admin/ReservationDialog";

export default function AdminReservations() {
  const [params] = useSearchParams();
  const dateParam = params.get("date") || "";
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    date_from: dateParam,
    date_to: dateParam,
    upcoming: !dateParam,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (!filters.q && !filters.date_from && !filters.date_to && filters.upcoming) params.upcoming = true;
      const { data } = await api.get("/admin/reservations", { params });
      setRows(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/reservations/${id}`, patch);
      await load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const grouped = rows.reduce((acc, r) => {
    (acc[r.date] = acc[r.date] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-reservations">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Prenotazioni</p>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Tutte le prenotazioni</h1>
        </div>
        <AdminButton variant="wood" onClick={() => setDialog({ mode: "create" })} data-testid="reservations-new">
          Nuova prenotazione
        </AdminButton>
      </div>

      <AdminCard title="Filtri">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <AdminField label="Cerca (nome, telefono, email, riferimento)">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-3.5 text-wood/40" />
              <input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className={`${adminInput} pl-9`}
                data-testid="filter-search"
                placeholder="Rossi, AT-…"
              />
            </div>
          </AdminField>
          <AdminField label="Stato">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className={adminInput}
              data-testid="filter-status"
            >
              <option value="">Tutti</option>
              {["pending", "confirmed", "completed", "cancelled", "no-show"].map((s) => (
                <option key={s} value={s}>
                  {STATUS_IT[s] || s}
                </option>
              ))}
            </select>
          </AdminField>
          <AdminField label="Dal">
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className={adminInput}
              data-testid="filter-from"
            />
          </AdminField>
          <AdminField label="Al">
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className={adminInput}
              data-testid="filter-to"
            />
          </AdminField>
        </div>
        <div className="mt-4 flex gap-2">
          <AdminButton onClick={load}>Applica</AdminButton>
          <AdminButton
            variant="ghost"
            onClick={() => setFilters({ q: "", status: "", date_from: "", date_to: "", upcoming: true })}
          >
            Azzera
          </AdminButton>
        </div>
      </AdminCard>

      {error && (
        <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p>
      )}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-foreground/60">
          <Loader2 size={15} className="animate-spin" /> Carichiamo…
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nessun risultato"
          description="Prova a cambiare i filtri o la ricerca."
          testId="reservations-empty"
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([date, list]) => (
            <div key={date}>
              <h2 className="font-serif text-2xl text-wood">{formatLongDate(date)}</h2>
              <ul className="mt-3 space-y-3">
                {list.map((res) => (
                  <ReservationRow
                    key={res.id}
                    res={res}
                    onUpdate={update}
                    onEdit={(r) => setDialog({ mode: "edit", reservation: r })}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <ReservationDialog
          {...dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            load();
          }}
        />
      )}
    </div>
  );
}
